// Measures how long a visitor actually spends in the demo.
//
// Firestore has no session concept, so each visit opens a demoSessions document
// and heartbeats into it. Duration is derived server-side-ish from
// startedAt → lastSeenAt rather than trusting a single "I left" write, which
// browsers routinely drop when a tab is closed.
//
// Heartbeats pause while the tab is hidden, so a portal left open in a
// background tab overnight doesn't report a 14-hour session.

import { doc, increment, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";
import { DEMO_NS } from "./demoConfig";

const HEARTBEAT_MS = 30000;
const SESSIONS = "artifacts/demo-analytics/public/data/demoSessions";
const USERS = "artifacts/demo-analytics/public/data/demoUsers";

export function startSessionTracking(db, user) {
  const sessionId =
    (crypto.randomUUID && crypto.randomUUID()) ||
    `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const ref = doc(db, SESSIONS, sessionId);
  const startedAt = Date.now();
  let activeMs = 0;
  let lastTick = startedAt;
  let timer = null;
  let stopped = false;

  setDoc(ref, {
    uid: user.uid,
    email: user.email || "",
    ns: DEMO_NS,
    startedAt: serverTimestamp(),
    lastSeenAt: serverTimestamp(),
    activeMs: 0,
    // Coarse device hint — useful for judging whether hoteliers open the demo
    // on a phone, without storing a full user-agent string.
    device: window.matchMedia("(max-width: 767px)").matches ? "mobile" : "desktop",
  }).catch(() => {});

  // Count a login on the user record too, so demoUsers shows repeat interest.
  updateDoc(doc(db, USERS, user.uid), { logins: increment(1) }).catch(() => {});

  const flush = () => {
    if (stopped) return;
    const now = Date.now();
    if (document.visibilityState === "visible") activeMs += now - lastTick;
    lastTick = now;
    updateDoc(ref, { lastSeenAt: serverTimestamp(), activeMs }).catch(() => {});
  };

  const onVisibility = () => {
    const now = Date.now();
    if (document.visibilityState === "hidden") {
      activeMs += now - lastTick;
      flush();
    }
    lastTick = now;
  };

  timer = setInterval(flush, HEARTBEAT_MS);
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("pagehide", flush);

  return () => {
    stopped = true;
    clearInterval(timer);
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("pagehide", flush);
  };
}
